import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography
} from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { login, otpLogin, WelcomeCoupon } from '../features/authSlice';
import { apiClient } from '../apiClient';
import { extractApiErrorMessage } from '../utils/errorHandler';
import Button from '../components/common/Button';
import { API_ENDPOINTS, ROUTES } from '../constants';

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  // OTP flow (default)
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [welcomeCoupon, setWelcomeCoupon] = useState<WelcomeCoupon | null>(null);
  const [copied, setCopied] = useState(false);
  const holdRedirect = useRef(false);

  // Email/password fallback
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!user || holdRedirect.current) return;
    if (redirectTo) {
      navigate(redirectTo);
    } else if (user.role === 'admin') {
      navigate(ROUTES.ADMIN);
    } else {
      navigate(ROUTES.ACCOUNT);
    }
  }, [user, navigate, redirectTo]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const cleanPhone = phone.replace(/\D/g, '').slice(-10);
  const phoneValid = /^[6-9]\d{9}$/.test(cleanPhone);

  const requestOtp = async () => {
    setOtpError(null);
    setSending(true);
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/otp/request`, { phone: cleanPhone });
      setIsNewUser(!response.data.existingUser);
      setDevMode(Boolean(response.data.devMode));
      setStep('otp');
      setOtp('');
      setResendIn(30);
    } catch (err) {
      setOtpError(extractApiErrorMessage(err, 'Could not send OTP. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const retryOtp = async (via: 'text' | 'voice') => {
    setOtpError(null);
    try {
      await apiClient.post(`${API_ENDPOINTS.AUTH}/otp/retry`, { phone: cleanPhone, via });
      setResendIn(30);
    } catch (err) {
      setOtpError(extractApiErrorMessage(err, 'Could not resend OTP. Please try again.'));
    }
  };

  const verifyOtp = async () => {
    setOtpError(null);
    holdRedirect.current = true;
    const result = await dispatch(
      otpLogin({ phone: cleanPhone, otp, name: isNewUser ? name.trim() : undefined })
    );
    if (otpLogin.rejected.match(result)) {
      holdRedirect.current = false;
      setOtpError(result.payload || 'Incorrect OTP. Please try again.');
      return;
    }
    if (result.payload.welcomeCoupon) {
      // Keep the user on this page until they've seen their coupon
      setWelcomeCoupon(result.payload.welcomeCoupon);
    } else {
      holdRedirect.current = false;
      navigate(redirectTo || (result.payload.user.role === 'admin' ? ROUTES.ADMIN : ROUTES.ACCOUNT));
    }
  };

  const closeWelcomeDialog = () => {
    setWelcomeCoupon(null);
    holdRedirect.current = false;
    navigate(redirectTo || ROUTES.ACCOUNT);
  };

  const copyCode = () => {
    if (welcomeCoupon) {
      navigator.clipboard?.writeText(welcomeCoupon.code).catch(() => undefined);
      setCopied(true);
    }
  };

  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 4,
          borderRadius: 4,
          boxShadow: '0 18px 45px rgba(0,0,0,0.08)',
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1 }}>
          {step === 'phone' ? 'Login or sign up' : 'Enter OTP'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {step === 'phone'
            ? 'Enter your mobile number and we’ll send you a one-time password. New here? Your account is created automatically.'
            : `We sent a code to +91 ${cleanPhone}`}
        </Typography>

        {step === 'phone' && (
          <>
            <TextField
              label="Mobile number"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && phoneValid && !sending) requestOtp();
              }}
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: <InputAdornment position="start">+91</InputAdornment>
              }}
              inputProps={{ inputMode: 'numeric', maxLength: 12 }}
            />
            {otpError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {otpError}
              </Alert>
            )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              loading={sending}
              disabled={!phoneValid}
              onClick={requestOtp}
              sx={{ mt: 3, mb: 1 }}
            >
              Get OTP
            </Button>
          </>
        )}

        {step === 'otp' && (
          <>
            {devMode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Dev mode: use OTP <strong>123456</strong>
              </Alert>
            )}
            {isNewUser && (
              <TextField
                label="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                autoFocus
                margin="normal"
                helperText="Looks like you're new here — welcome!"
                inputProps={{ maxLength: 80 }}
              />
            )}
            <TextField
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && otp.length >= 4) verifyOtp();
              }}
              fullWidth
              autoFocus={!isNewUser}
              margin="normal"
              inputProps={{
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
                style: { letterSpacing: '0.4em', fontSize: 20 }
              }}
            />
            {otpError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {otpError}
              </Alert>
            )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              loading={loading}
              disabled={otp.length < 4 || (isNewUser && !name.trim())}
              onClick={verifyOtp}
              sx={{ mt: 2, mb: 1.5 }}
            >
              Verify & continue
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {resendIn > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Resend in {resendIn}s
                </Typography>
              ) : (
                <>
                  <MuiLink component="button" type="button" variant="caption" onClick={() => retryOtp('text')}>
                    Resend OTP
                  </MuiLink>
                  <MuiLink component="button" type="button" variant="caption" onClick={() => retryOtp('voice')}>
                    Get OTP via call
                  </MuiLink>
                </>
              )}
              <MuiLink
                component="button"
                type="button"
                variant="caption"
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                  setOtpError(null);
                }}
              >
                Change number
              </MuiLink>
            </Box>
          </>
        )}

        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            color="text.secondary"
            onClick={() => setShowEmailLogin((v) => !v)}
          >
            {showEmailLogin ? 'Hide email login' : 'Login with email & password instead'}
          </MuiLink>
          <Collapse in={showEmailLogin}>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                dispatch(login({ email, password }));
              }}
              sx={{ mt: 1 }}
            >
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
                size="small"
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
              <Button type="submit" variant="outlined" color="primary" fullWidth loading={loading} sx={{ mt: 1.5 }}>
                Log in with password
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Prefer email signup?{' '}
                <RouterLink
                  to={redirectTo ? `${ROUTES.REGISTER}?redirect=${encodeURIComponent(redirectTo)}` : ROUTES.REGISTER}
                >
                  Sign up here
                </RouterLink>
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Dialog open={Boolean(welcomeCoupon)} onClose={closeWelcomeDialog} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Welcome to Vediluxe! 🎉
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Here&apos;s {welcomeCoupon?.percent}% off your first order — just for you:
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2.5,
              py: 1.25,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              mb: 1
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}>
              {welcomeCoupon?.code}
            </Typography>
            <IconButton size="small" onClick={copyCode} aria-label="Copy coupon code">
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
          {copied && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
              Copied!
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            One-time use, valid until{' '}
            {welcomeCoupon ? new Date(welcomeCoupon.validUntil).toLocaleDateString('en-IN') : ''}.
            Apply it at checkout.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <MuiButton variant="contained" onClick={closeWelcomeDialog}>
            Start shopping
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
